import type {
  PatientMedicalDataQuery,
  PatientMedicalHistoryTimelineQuery,
  PatientProblemsQuery,
} from '~/api/graphql/generated/types'

export interface MedicalHistoryData {
  currentVitals: {
    bodyConditionScore: number | null
    weight: unknown | null
  } | null
  patient: {
    dateOfBirth: string | null
    id: string
    name: string
    species: { id: string; name: string } | null
  }
  prescriptions: Array<{
    id: string
    medication: {
      masterName: string | null
      variationName: string | null
    } | null
    prescribedDate: string | null
    rxNo: string | null
    status: string | null
  }>
  problems: Array<{
    breadcrumb: string | null
    id: string
    name: string
    nameTranslation: string
    type: string | null
  }>
  timeline: Array<{
    data: Record<string, unknown>
    date: string | null
    id: string
    type: string
  }>
  vitals: Array<{
    createdAt: string | null
    id: string
    temperature: unknown | null
    weight: unknown | null
  }>
}

export const transformMedicalHistoryData = (
  timelineData: PatientMedicalHistoryTimelineQuery['timeline'] | undefined,
  patientData: PatientMedicalDataQuery['patient'] | undefined,
  problemsData: PatientProblemsQuery['problemCatalog'] | undefined,
): MedicalHistoryData => {
  // Transform timeline data
  const timelineEntries =
    timelineData?.data
      ?.filter((entry): entry is NonNullable<typeof entry> => entry !== null)
      .map((entry) => {
        const baseEntry = {
          type: entry.__typename || 'Unknown',
          id: '',
          date: entry.timelineDate || null,
          data: {} as Record<string, unknown>,
        }

        // Extract type-specific fields
        switch (entry.__typename) {
          case 'Prescription':
            baseEntry.id = entry.id
            baseEntry.data = {
              name: entry.name,
              rxNo: entry.rxNo,
              prescribedDate: entry.prescribedDate,
              status: entry.state?.name || null,
              medication: entry.drugInfo
                ? {
                    masterName: entry.drugInfo.masterName,
                    variationName: entry.drugInfo.variationName,
                  }
                : null,
              notes: entry.notes,
            }
            break
          case 'DiagnosisHistory':
            baseEntry.id = entry.id
            baseEntry.data = {
              diagnosisId: entry.diagnosisId,
              date: entry.date,
              diagnosedBy: entry.diagnosedBy,
              notes: entry.notes,
            }
            break
          case 'DiagnosisLog':
            baseEntry.id = entry.id
            baseEntry.data = {
              description: entry.description,
              diagnosisNotes: entry.diagnosisNotes,
              vetId: entry.vetId,
            }
            break
          case 'ProblemLog':
            baseEntry.id = entry.id
            baseEntry.data = {
              name: entry.name,
              problemLogNotes: entry.problemLogNotes,
            }
            break
          case 'LabTestLog':
            baseEntry.id = entry.id
            baseEntry.data = {
              name: entry.name,
              date: entry.date,
              notes: entry.notes,
              labType: entry.labType,
              labVendor: entry.labVendor,
            }
            break
          case 'VaccinationDetails':
            baseEntry.id = entry.id
            baseEntry.data = {
              procedureName: entry.procedureName,
              dateGiven: entry.dateGiven,
              notes: entry.notes,
            }
            break
          default:
            // For other types (Appointment, Invoice, etc.), just capture what we can
            baseEntry.data = {}
        }

        return baseEntry
      })
      .filter((entry) => entry.id !== '') || []

  // Transform patient data
  const patient = patientData
    ? {
        id: patientData.id,
        name: patientData.name,
        dateOfBirth: patientData.dateOfBirth
          ? new Date(patientData.dateOfBirth).toISOString()
          : null,
        species: patientData.species
          ? {
              id: patientData.species.id,
              name: patientData.species.name,
            }
          : null,
      }
    : {
        id: '',
        name: '',
        dateOfBirth: null,
        species: null,
      }

  const currentVitals = patientData?.currentVitals
    ? {
        weight: patientData.currentVitals.weight,
        bodyConditionScore:
          patientData.currentVitals.bodyConditionScore ?? null,
      }
    : null

  const vitals =
    patientData?.vitals?.data
      ?.filter((vital): vital is NonNullable<typeof vital> => vital !== null)
      .map((vital) => ({
        id: vital.id,
        weight: vital.weight?.value ?? null,
        temperature: vital.temperature?.value ?? null,
        createdAt: vital.createdAt
          ? new Date(vital.createdAt).toISOString()
          : null,
      })) || []

  const prescriptions =
    patientData?.prescriptions?.data
      ?.filter(
        (prescription): prescription is NonNullable<typeof prescription> =>
          prescription !== null,
      )
      .map((prescription) => ({
        id: prescription.id,
        rxNo: prescription.rxNo ?? null,
        status: prescription.state?.name || null,
        prescribedDate: prescription.prescribedDate
          ? new Date(prescription.prescribedDate).toISOString()
          : null,
        medication: prescription.drugInfo
          ? {
              masterName: prescription.drugInfo.masterName ?? null,
              variationName: prescription.drugInfo.variationName ?? null,
            }
          : null,
      })) || []

  // Transform problems data
  const problems =
    problemsData?.problems
      ?.filter(
        (problem): problem is NonNullable<typeof problem> => problem !== null,
      )
      .map((problem) => ({
        id: problem.id,
        name: problem.name,
        nameTranslation: problem.nameTranslation,
        type: problem.type || null,
        breadcrumb: problem.breadcrumb || null,
      })) || []

  return {
    patient,
    currentVitals,
    vitals,
    prescriptions,
    problems,
    timeline: timelineEntries,
  }
}
